import { Injectable } from '@angular/core';
import { ConfirmPaymentComponent } from '../components/modals/confirm-payment/confirm-payment.component';
import { AlertController, ModalController, NavController } from '@ionic/angular';
import { GameTicket, GameType } from '../models/game.model';
import { WagerPayload } from '../models/wager.model';
import { WalletApiService } from './wallet-api.service';
import { WalletService } from './wallet.service';
import { Logger } from '../logger.service';
import { WalletTypeService } from './wallet-type.service';
import { Credentials, CredentialsService } from '@app/auth';
import { Wallet } from '../models/wallet.model';
import { TransactionService } from './transaction.service';
import { Transaction } from '../models/transaction.model';
import { DateService } from './date.service';
import { HttpService } from './http.service';
import { environment } from '@env/environment';
import { Timestamp, serverTimestamp } from 'firebase/firestore';
import { GeneralService } from './general.service';
import { GameHomeService } from './game-home.service';
import { TranslateService } from '@ngx-translate/core';
import { LotenalService } from './lotenal.service';
import { GameRestrictionService } from './game-restriction.service';
import { TrackingService } from './tracking.service';
import { PurchaseIGTResponse } from '../models/purchase.model';
import { CampaignService } from './campaign.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { SorteoService } from './sorteo.service';

const log = new Logger('lotenal-payment-service');

@Injectable({
  providedIn: 'root',
})
export class LotenalPaymentService {
  pathSkip = 'skip-confirmation';
  pathToPay = 'to-pay';

  draw: any[] = [];
  errorIGT =
    'Request cannot be processed because (i) it does not fall under permitted time window, or (ii) the host is signed off.';
  errorMessages = {
    terminalInUse: 'terminalIsInUse',
    parameterIssue: 'Translet parsing issue, refer logs for more details',
    terminalClosed: this.errorIGT,
    invalidBoardData: 'errorInvalidBoardData',
    isNull: null,
  };
  constructor(
    private dateS: DateService,
    private httpS: HttpService,
    private navCtrl: NavController,
    private walletS: WalletService,

    private lotenalS: LotenalService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,

    private walletApiS: WalletApiService,
    private translateS: TranslateService,
    private walletTypeS: WalletTypeService,
    private credentials: CredentialsService,
    private transactionS: TransactionService,
    public gameRestrictionS: GameRestrictionService,
    public trackingS: TrackingService,
    private campaignS: CampaignService,
    private afs: AngularFirestore
  ) {}

  async goToPayment(amount: number, type: GameType, tickets: GameTicket[], data: WagerPayload[], drawLotenal?: any) {
    //console.log('drawLotenal: ', drawLotenal);
    let isClose = false;
    // obtener el balance de la wallet
    const wallets = await this.walletS.getByCustomerId(this.credentials.credentials?.id!);

    const balance = wallets.reduce((a, b) => a + b.balance, 0);

    if (balance < amount) {
      const alert = await this.alertCtrl.create({
        header: 'Fondos insuficientes',
        message: 'Es necesario realizar una recarga',
        buttons: ['Ok'],
      });

      await alert.present();

      await alert.onWillDismiss();

      const id = 'openFunds';
      document.getElementById(id)?.click();

      return;
    }

    const skip = JSON.parse(localStorage.getItem(this.pathSkip) ?? 'false') as boolean;
    let role = 'skip';

    if (!skip) {
      const modal = await this.modalCtrl.create({
        component: ConfirmPaymentComponent,
        componentProps: {
          amount,
          tickets: tickets,
          type,
          isCoperacha: false,
          isRetro: false,
          isMediaSemana: false,
          touches: [],
          tracking: this.trackingS,
        },
        cssClass: 'confirmpayment',
        backdropDismiss: false,
      });

      await modal.present();
      role = (await modal.onWillDismiss()).role ?? 'close';
      //console.log('role: ', role);
      if (role === 'close') {
        isClose = true;
      }
    }

    if (role === 'skip' || role === 'accept') {
      const value = tickets.map((item: any) => item);

      // Verificar para cada ticket si se puede realizar la petición
      const canGoToExecuteResults = await Promise.all(
        value.map((item) => {
          //console.log('Processing item:', item);
          return this.canGoToExecute(item[0].dates[0]);
        })
      );

      // Si todos los tickets pueden ejecutarse, proceder
      if (canGoToExecuteResults.every((result) => result)) {
        if (drawLotenal) this.draw = drawLotenal;

        // Guardar todos los WagerPayload en localStorage
        this.saveLocalData(amount, type, tickets, data);
        this.navCtrl.navigateForward(['/', 'payment']);
      }
    }
    return isClose;
  }

  async canGoToExecute(d: Date) {
    //console.log('DATE: ', d);
    const firstDate = new Date(d);
    //console.log('firstDate: ', firstDate);
    const now = await this.dateS.dateNow();

    firstDate.setSeconds(firstDate.getSeconds() - environment.draw.endSales);

    const canGo = now.getTime() < firstDate.getTime();

    log.info(canGo);

    if (!canGo) {
      const alert = await this.alertCtrl.create({
        header: this.translateS.instant('error.payment.draw-closed'),
        buttons: [this.translateS.instant('Ok')],
        // cssClass: 'alert'
      });

      await alert.present();
    }

    return canGo;
  }

  saveLocalData(amount: number, type: GameType, tickets: GameTicket[], data: WagerPayload[]): boolean {
    try {
      const payload = {
        type,
        amount,
        data,
        tickets,
      };

      const encoded = btoa(JSON.stringify(payload));

      sessionStorage.setItem(this.pathToPay, encoded);
      return true;
    } catch (error) {
      log.error(error);
    }
    return false;
  }

  getLocalData(): {
    amount: number;
    type: GameType;
    tickets: GameTicket[];
    data: WagerPayload[];
  } | null {
    try {
      const data = sessionStorage.getItem(this.pathToPay) ?? btoa(JSON.stringify(null));

      const objeto = JSON.parse(atob(data));

      return objeto
        ? {
            ...objeto,
            dates: objeto.dates.map((v: string) => new Date(v)),
          }
        : objeto;
    } catch (error) {
      log.error(error);
    }
    return null;
  }

  removeLocalData() {
    localStorage.removeItem(this.pathToPay);
  }

  async processPayment(
    amount: number,
    type: GameType,
    tickets: GameTicket[],
    data: WagerPayload[]
  ): Promise<Transaction | null> {
    //obtener las credenciales
    const credentials = this.credentials.credentials!;
    const id = credentials.id;

    //obtener las wallet del customer
    const wallets = await this.walletS.getByCustomerId(this.credentials.credentials?.id!);

    // obtener los diferentes tipos de loteria con los que se cuentan
    const walletTypes = await this.walletTypeS.getAll();

    // filtrar el tipo e la loteria por la de "loteria"
    const loteria = walletTypes.find((w) => w.walletName === 'loteria');

    // obtener la wallet de "loteria" para el customer actual
    let wloteria = wallets.find((w) => w.walletName === 'loteria');

    // si no existe la wallet se creara una (pero ya no es necesario porque se crea al consultar el saldo)
    if (!wloteria) {
      const data = await this.walletApiS.createWallet({
        walletTypeId: loteria?.uid!,
        currency: 'MXN',
      });

      if (data?.id) {
        const wl = await this.walletS.getById(data.id);
        if (wl) {
          wloteria = wl;
          wallets.push(wl);
        }
      }
    }

    // comprobar si fue posible crear la wallet u obtenerla
    if (!wloteria || !loteria) {
      log.error('error-wallet-not-found');
      return null;
    }

    //obtener wallet award
    const waward = wallets.find((w) => w.walletName === 'award');

    const walletUsed = waward
      ? waward.balance >= amount
        ? waward.id
        : waward.balance
        ? `${waward.id}/${wloteria.id}`
        : wloteria.id
      : wloteria.id;

    // revisar que se tengan los fondos suficientes para realizar la compra
    if (wallets.reduce((a, b) => a + b.balance, 0) < amount) {
      log.error('error-balance-lower-than-cost');
      return null;
    }

    const values: any = tickets[0] as { dates: Date[] };

    //console.log('values: ', values);
    // verificar si se puede realizar la peticion

    const value = tickets.map((item: any) => item);

    //console.log('tickets proccess: ', value);

    // Verificar para cada ticket si se puede realizar la petición
    const canGoToExecuteResults = await Promise.all(
      value.map((item) => {
        //console.log('Processing item:', item);
        return this.canGoToExecute(item[0].dates[0]);
      })
    );

    // Solo continuar si TODAS las verificaciones son true
    const canProceed = canGoToExecuteResults.every((result) => result);

    if (canProceed) {
      const transaction = await this.executeCloudPayment({
        amount,
        tickets,
        data,
        type,
      });
      this.trackTicketPaid(type, data, amount, transaction?.uid ?? '');

      if (!transaction) {
        this.navCtrl.navigateBack(['/', 'game', type], {
          replaceUrl: true,
        });
      }

      if (transaction && amount > transaction.amount) {
        const refund = amount - transaction.amount;
        this.trackRefundWallet(refund, transaction?.uid ?? '');
      }
      return transaction ?? null;
    }

    return null;
  }

  private async executeCloudPayment(body: {
    amount: number;
    type: GameType;
    tickets: GameTicket[];
    data: WagerPayload[];
  }) {
    try {
      console.log('before pay reservation: ', body);
      const response = (await this.httpS.post(`${environment.api.tradicional.purchase}/process`, {
        ...body,
        drawLotenal: this.draw,
      })) as PurchaseIGTResponse | null;

      log.info(JSON.stringify(response, null, 2));
      log.info('response', response);

      if (response) {
        const existsAlerts = response.alerts.some((a) => a !== null);
        const alerts = response.alerts.filter((a) => a !== null && a.showAlert);

        if (response.alerts && response.alerts.length && existsAlerts && alerts.length) {
          for (const al of alerts) {
            const alert = await this.alertCtrl.create({
              header: this.translateS.instant(al?.alertHeader ?? ''),
              message: this.translateS.instant(al?.alertMessage ?? ''),
              buttons: [this.translateS.instant('Close')],
            });

            await alert.present();
          }

          if (!response.data || !response.data.wager.some((w: any) => w && w !== null)) {
            return null;
          }
        }

        const transaction = response.data;

        if (!transaction && !alerts.length) {
          const alert = await this.alertCtrl.create({
            header: 'Error',
            message: 'Estimado cliente, por el momento no podemos atender su solicitud, intente mas tarde.',
            buttons: [this.translateS.instant('Close')],
          });

          await alert.present();
        }

        if (transaction) {
          transaction.createdAt = new Timestamp(
            (transaction.createdAt as any)._seconds,
            (transaction.createdAt as any)._nanoseconds
          );
          transaction.modifiedAt = new Timestamp(
            (transaction.modifiedAt as any)._seconds,
            (transaction.modifiedAt as any)._nanoseconds
          );
        }

        for (let index = 0; index < response.data?.data.data.length; index++) {
          const element = response.data?.data.data[index].gameData.lnRequestCR.idTransaccion;

          this.removeReserv(element);
        }

        return transaction ?? null;
      }

      const alert = await this.alertCtrl.create({
        header: 'Error',
        message: 'Estimado cliente, por el momento no podemos atender su solicitud, intente mas tarde.',
        buttons: [this.translateS.instant('Close')],
      });

      await alert.present();
    } catch (error) {
      log.error(error);

      const alert = await this.alertCtrl.create({
        header: 'Error',
        message: 'Estimado cliente, por el momento no podemos atender su solicitud, intente mas tarde.',
        buttons: [this.translateS.instant('Close')],
      });

      await alert.present();
    }
    return null;
  }

  async removeReserv(idTransaction: string) {
    console.log(idTransaction);
    try {
      const querySnapshot = await this.afs.firestore
        .collection('lotenalRelease')
        .where('idTransaccion', '==', idTransaction)
        .limit(1)
        .get();

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const docId = doc.id;

        await this.afs.collection('lotenalRelease').doc(docId).delete();

        console.warn(' document found and delete.');
      } else {
        console.warn('No document found with that transaction ID.');
      }
    } catch (error) {
      console.error('Error fetching or deleting document:', error);
    }
  }

  /**
   * Realiza la disminucion del balance en la wallet
   * @param tickets los tickes de los juegos
   * @param amount el monto a ddescontar de la wallet
   * @param balance el b alance de la wallet
   * @param type tipo de juego
   * @param data array de las peticiones que se realizaran
   * @param credentials las credenbciales del usuario
   * @param wallet la wallet del customer
   * @returns La transaccion para agregar los datos y el nuevo valor de balance de la wallet o null si existe algun error
   */
  private async reduceAmountFromBalance(
    tickets: GameTicket[],
    amount: number,
    balance: number,
    type: GameType,
    data: WagerPayload[],
    credentials: Credentials,
    wallet: Wallet
  ): Promise<{
    transaction: Transaction;
    result: number;
  } | null> {
    if (amount > balance) {
      log.error('error-balance-lower-than-cost-on-reducea-amount');
      return null;
    }

    const result = balance - amount; //this.walletApiS.
    // const updated = true; // this.walletApiS
    // this.walletApiS.savePaymentDataForUserRecharge()
    const product = data[0].product;
    const transaction: Transaction = {
      // id: '',
      amount,
      currency: 'MXN',
      type: 'wallet-purchase-' + product,
      createdAt: serverTimestamp() as any,
      isAutomaticPurchase: true,
      customerId: this.walletS.getRef(credentials.id, 'customers') as any,
      walletId: this.walletS.getRef(wallet.id, 'wallets') as any,
      status: 'pending',
      gameId: this.walletS.getRef('123,', 'games') as any,
      discountId: this.walletS.getRef('123,', 'discounts') as any,
      logs: [],
      data: {
        tickets,
        amount,
        type,
        data,
        balanceStart: balance,
        balanceEnd: balance - amount,
        code: this.credentials.credentials?.id,
      },
    };

    const doc = await this.transactionS.create(transaction);

    // balance = balance - amount;

    await this.walletS.update(wallet.id, {
      balance: result,
      modified: serverTimestamp(),
    });

    // if (updated) {
    return {
      transaction: {
        ...((await doc.get()).data() as Transaction),
        data: typeof transaction.data === 'string' ? JSON.parse(transaction.data) : transaction.data,
        uid: doc.id,
      },
      result,
    };
    // } else {
    //   return null;
    // }
  }

  async confirmReservation(body: any[], drawActual: any[]): Promise<({ [name: string]: string } | null)[]> {
    const results: ({ [name: string]: string } | null)[] = [];
    try {
      for (const item of body) {
        const codEstado = 9; // Fijo para CDMX
        const drawType = item?.product;
        const request = item?.gameData;
        const url = `${environment.api.tradicional.purchases}/confirmReservation`;
        const response = await this.httpS.post(url, request);
        const responses = Array.isArray(response) ? response : [response];
        if (
          (response as any)?.lnResponseCR?.responseHeadTerminal?.respCodeDescription?.includes(
            'Formato de parámetro incorrecto'
          ) ||
          (response as any)?.lnResponseCR?.responseHeadTerminal?.respCodeDescription?.includes(
            'Parámetro de entrada incorrecto'
          )
        ) {
          results.push(null);
          const transactionIds = body.map((item) => item?.gameData?.lnRequestCR?.idTransaccion).filter((id) => id);
          for (const id of transactionIds) {
            await this.lotenalS.releaseTickets(Number(id), item.drawNumber);
          }
          continue;
        }

        await this.lotenalS.savePurchase(response, request, drawType, codEstado, drawActual);
        results.push(...(responses as ({ [name: string]: string } | null)[]));
      }

      return results;
    } catch (error) {
      console.error(error);
      this.gameRestrictionS.validateMessageIgt({ errorRetries: true });

      // Crear un mapa de idTransaccion a drawNumber para cada item en body
      const transactionMap = body.reduce((acc, item) => {
        const id = item?.gameData?.lnRequestCR?.idTransaccion;
        if (id) {
          acc[id] = item.drawNumber;
        }
        return acc;
      }, {} as Record<string, any>);

      const transactionIds = Object.keys(transactionMap).filter((id) => id);
      console.log('Failed transaction IDs:', transactionIds);

      for (const id of transactionIds) {
        await this.lotenalS.releaseTickets(Number(id), transactionMap[id]);
      }
      this.trackTicketReceivednt(body[0].product, ' ', 400, 'bad request');

      return [null];
    }
  }

  /**
   *
   * @param cashBack monto a aumentar
   * @param tickets tickets de los juegos participantes
   * @param amount monto de la venta calculada previamente
   * @param balance balance actual de la billetera
   * @param type tipo de gruego, tris, melate, etc
   * @param data informacion de payload wawger request
   * @param credentials credentials del usuario
   * @param wallet billetera actual
   * @param response response de wager execute
   * @returns
   */
  private async cashBack(
    cashBack: number,
    tickets: GameTicket[],
    amount: number,
    balance: number,
    type: GameType,
    data: WagerPayload[],
    credentials: Credentials,
    wallet: Wallet,
    response: ({
      [name: string]: string;
    } | null)[]
  ) {
    try {
      const result = balance + cashBack;
      const product = data[0].product;
      const transaction: Transaction = {
        // id: '',
        amount: cashBack,
        currency: 'MXN',
        type: 'wallet-cashback-' + product,
        createdAt: serverTimestamp() as any,
        isAutomaticPurchase: true,
        customerId: this.walletS.getRef(credentials.id, 'customers') as any,
        walletId: this.walletS.getRef(wallet.id, 'wallets') as any,
        status: 'completed',
        gameId: this.walletS.getRef('123,', 'games') as any,
        discountId: this.walletS.getRef('123,', 'discounts') as any,
        logs: [],
        data: {
          tickets,
          amount,
          type,
          data,
          balanceStart: balance,
          balanceEnd: result,
          code: this.credentials.credentials?.id,
          response,
        },
      };

      await this.transactionS.create(transaction);
      console.log('transaction: ', transaction);
      this.trackRefundWallet(amount, transaction.uid ?? '');
      // balance = balance - amount;

      await this.walletS.update(wallet.id, {
        balance: result,
        modified: serverTimestamp(),
      });
      return true;
    } catch (error) {
      log.error(error);
    }
    return false;
  }

  trackTicketPaid(gameType: string, data: any, amount: number, walletId: string) {
    // 1. Evento principal: ticket pagado
    this.trackingS.trackEvent(
      'purchase',
      {
        game_id: gameType,
        transaction_id: walletId,
        currency: 'MXN',
        items: [{ item_name: gameType, quantity: 1 }],
        value: amount,
      },
      false,
      true
    );

    // 2. Disparo de campaign_conversion si hay UTM
    const utmParams = this.campaignS.getUTMParams();
    const hasUTM = utmParams && Object.values(utmParams).some((v) => v !== null);

    if (hasUTM) {
      this.trackingS.trackEvent(
        'campaign_conversion',
        {
          ...utmParams,
          conversion_type: 'compra',
          game_id: gameType,
          total_amount: amount,
          transaction_id: walletId,
          draw_type: data?.drawType || null,
        },
        false,
        true
      );
    }
  }

  trackRefundWallet(amount: number, requestId: string) {
    this.trackingS.trackEvent(
      'refund_requested',
      {
        amount: amount,
        request_id: requestId,
      },
      false,
      false
    );
  }
  trackTicketReceivednt(gameId: string, transactionId: string, error: number, errorAgent: string) {
    this.trackingS.trackEvent(
      'ticket_noreceived',
      {
        game_id: gameId,
        transactionId: transactionId,
        error: error,
        error_agent: errorAgent,
      },
      false,
      false
    );
  }
}
